<script setup lang="ts">
import type { Category, Content, GlobalSetting } from '~/types/blog'
import { normalizeFieldBool, normalizeFieldJson, normalizeFieldText } from '~/composables/useCustomFields'

type CachedSiteData = {
  setting: GlobalSetting
} | null

type ErrorShellData = {
  setting: GlobalSetting
  categories: Category[]
  pages: Content[]
} | null

const blogApi = useBlogApi()
const siteDataState = useState<CachedSiteData>('site-data', () => null)
const sidebarCached = useNuxtData<{ categories?: Category[]; pages?: Content[] }>('blog-sidebar')

const { data: shellData } = await useAsyncData<ErrorShellData>('error-shell-data', async () => {
  try {
    const [setting, categories, pages] = await Promise.all([
      blogApi.getGlobalSetting(),
      blogApi.getCategoryTree(),
      blogApi.getContentMetaList({ type: 'page', page: 1, pageSize: 200 })
    ])
    return { setting, categories, pages: pages.list || [] }
  } catch {
    return null
  }
})

const setting = computed(() => shellData.value?.setting || siteDataState.value?.setting || null)
const fields = computed(() => setting.value?.customFields)
const hasShellData = computed(() => !!setting.value)

const siteName = computed(() => normalizeFieldText(fields.value, 'siteName', ''))
const siteSubtitle = computed(() => normalizeFieldText(fields.value, 'siteSubtitle', ''))
const siteLogo = computed(() => normalizeFieldText(fields.value, 'siteLogo', ''))
const showNavSiteName = computed(() => normalizeFieldBool(fields.value, 'showNavSiteName', true))
const isSideNav = computed(() => normalizeFieldText(fields.value, 'navMode', 'top') === 'side')
const icp = computed(() => normalizeFieldText(fields.value, 'icp', ''))
const publicSecurityRecord = computed(() => normalizeFieldText(fields.value, 'publicSecurityRecord', ''))
const footerText = computed(() => normalizeFieldText(fields.value, 'footerText', ''))
const footerExternalLinks = computed(() =>
  normalizeFieldJson<Array<string | { label?: string; url?: string }>>(fields.value, 'footerExternalLinks', [])
)
const showFeedLink = computed(() => normalizeFieldBool(fields.value, 'showFeedLink', true))
const showSitemapLink = computed(() => normalizeFieldBool(fields.value, 'showSitemapLink', true))
const navContentLimit = computed(() => Math.max(1, Number(normalizeFieldText(fields.value, 'navContentLimit', '10')) || 10))
const navItems = computed(() =>
  normalizeFieldJson<Array<{ name?: string; path?: string; sort?: number; newTab?: boolean }>>(fields.value, 'navItems', [])
    .filter(item => String(item?.name || '').trim() && String(item?.path || '').trim())
    .sort((a, b) => (a.sort || 0) - (b.sort || 0))
)
const categories = computed(() => shellData.value?.categories || sidebarCached.data.value?.categories || [])
const pageLinks = computed(() => shellData.value?.pages || sidebarCached.data.value?.pages || [])
</script>

<template>
  <div class="site-shell" :class="{ 'site-shell-side': hasShellData && isSideNav }">
    <BlogSiteHeader
      v-if="hasShellData"
      :site-name="siteName"
      :site-subtitle="siteSubtitle"
      :site-logo="siteLogo"
      :show-site-name-in-nav="showNavSiteName"
      :custom-fields="fields"
      :nav-items="navItems"
      :page-links="pageLinks"
      :categories="categories"
      :nav-content-limit="navContentLimit"
    />

    <div class="site-body">
      <main class="container error-layout-main">
        <section class="error-layout-pane">
          <slot />
        </section>
      </main>

      <BlogSiteFooter
        v-if="hasShellData"
        :text="footerText"
        :icp="icp"
        :public-security-record="publicSecurityRecord"
        :external-links="footerExternalLinks"
        :show-feed-link="showFeedLink"
        :show-sitemap-link="showSitemapLink"
      />
    </div>
    <BlogBackToTop />
  </div>
</template>

<style scoped>
.error-layout-main {
  margin-top: 30px;
  margin-bottom: 46px;
}

.error-layout-pane {
  min-width: 0;
}

@media (max-width: 960px) {
  .error-layout-main {
    margin-top: 94px;
  }
}
</style>
